import React, { useState, useEffect, Fragment } from 'react';
import { useHistory, Link, useParams } from 'react-router-dom';
import Select from 'react-select';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, Button, CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CopyrightIcon from '@mui/icons-material/Copyright';
// Assume fetchData and postData are defined elsewhere
import { fetchData, updateData, postData } from '../../../apiService';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import AsyncSelect from "react-select/async";  // ✅ instead of AsyncCreatableSelect
import AsyncCreatableSelect from 'react-select/async-creatable';

const PurchaseOrderDetail = () => {
   const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const history = useHistory();
    const { id } = useParams();
    const { token, userId, companyId, isAuthenticated, logout } = useAuth();

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [durationTypes, setDurationTypes] = useState([]);

    const [showAMC, setShowAMC] = useState(false);
    const [showStamping, setShowStamping] = useState(false);

    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchDurationTypes();
        setIsLoadingData(false);
         fetchCustomers();


        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, token, id]);


    useEffect(() => {
        if (!id) return;
        
        fetchData('PurchaseOrder', `/PurchaseOrder/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    const data = response.response_data;
                    
                    // Set form data with properly formatted dates
                    setFormData({
                        Id: data.Id,
                        CompanyId: data.CompanyId || companyId,
                        CustomerId: data.CustomerId,
                        PONumber: data.PONumber,
                        FinYear: data.FinYear,
                        PODate: data.PODate ? data.PODate.split("T")[0] : "",
                        StampTypeId: data.StampTypeId || "",
                        StampingFromDate: data.StampingFromDate ? data.StampingFromDate.split("T")[0] : "",
                        StampingToDate: data.StampingToDate ? data.StampingToDate.split("T")[0] : "",
                        AMCTypeId: data.AMCTypeId || "",
                        AMCFromDate: data.AMCFromDate ? data.AMCFromDate.split("T")[0] : "",
                        AMCToDate: data.AMCToDate ? data.AMCToDate.split("T")[0] : "",
                        SubcontractId: data.SubcontractorId || "",
                        StateId: data.StateId || "",
                        StateName: data.StateName || "",
                        StampingStatusId: data.StampingStatusId || 1,
                        AmcStatusId: data.AmcStatusId || 1,
                        IsActive: data.IsActive,
                        CreatedDate: data.CreatedDate ? data.CreatedDate.split("T")[0] : "",
                        CreatedBy: data.CreatedBy,
                        UpdatedBy: userId,
                        UpdatedDate: new Date().toISOString().split("T")[0]
                    });

                    // Set customer dropdown
                    if (data.CustomerId && data.CustomerName) {
                        setSelectedCustomer({
                            value: data.CustomerId,
                            label: data.CustomerName
                        });
                    }

                    // Fetch and set state name if StateId exists but StateName is null
                    if (data.StateId && !data.StateName) {
                        fetchData('SubContractAPI', `/State/${companyId}`, token)
                            .then(stateResponse => {
                                if (stateResponse.responseCode === '1111') {
                                    const state = stateResponse.response_data.find(s => s.Id === data.StateId);
                                    if (state) {
                                        setFormData(prev => ({
                                            ...prev,
                                            StateName: state.Name
                                        }));
                                    }
                                }
                            })
                            .catch(err => console.error('Error fetching state:', err));
                    }

                    // Show AMC/Stamping sections if data exists
                    if (data.StampingFromDate || data.StampingToDate) {
                        setShowStamping(true);
                    }
                    if (data.AMCFromDate || data.AMCToDate || data.AMCTypeId) {
                        setShowAMC(true);
                    }
                }
                setIsLoadingData(false);
            })
            .catch((error) => {
                console.error('Error fetching Purchase Order:', error);
                setErrorMessage('Failed to load purchase order');
                setIsLoadingData(false);
            });
    }, [id]);


    useEffect(() => {
        fetchSubContracters();
    }, []);

    //Subcontracters Fetching

    const [SubcontractersOptions, setSubcontractersOptions] = useState([]);
    const [selectedSubcontracters, setSelectedSubcontracters] = useState("");

    const fetchSubContracters = async () => {
        try {

            const response = await fetchData('SubContractAPI', `/${companyId}`, token);
            if (response.responseCode === '1111') {

                const options = response.response_data.map(subcontract => ({
                    value: subcontract.Id,
                    label: subcontract.Name
                }));
                setSubcontractersOptions(options);
            }
        } catch (error) {
            console.error('Error fetching subcontracters:', error);
        }
    };
    const handleSubcontractersChange = (selectedOption) => {
        setSelectedSubcontracters(selectedOption);
        setFormData(prevData => ({
            ...prevData,
            UpdatedBy: userId,
            CompanyId: companyId,
            SubcontractId: selectedOption.value,
        }));
    };

    //Duration Types Fetching
    const fetchDurationTypes = async () => {
        try {
            const response = await fetchData('PurchaseOrder', `/DurationType/${companyId}`, token);
            if (response.responseCode === '1111') {
                const options = response.response_data.map(durationType => ({
                    value: durationType.Id,
                    label: durationType.DurationTypes
                }));
                setDurationTypes(options);
            }
        } catch (error) {
            console.error('Error fetching duration types:', error);
        }
    };

    const fetchOptions = async (inputValue) => {
        if (inputValue.length < 3) return [];
        try {
            const response = await fetchData('MastersAPI', `/Customer/${companyId}/${inputValue}`, token);
            if (response.responseCode === '1111' && Array.isArray(response.response_data)) {
                return response.response_data.map(item => ({
                    value: item.Id || item.id,
                    label: item.Name || item.name,
                    stateId: item.StateId || item.stateId || '',
                    stateName: item.StateName || item.stateName || '',
                }));
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
        }
        return [];
    };

    const handleCustomerSelect = (selectedOption) => {
        console.log('Customer selected:', selectedOption);
        setSelectedCustomer(selectedOption);
        
        if (selectedOption) {
            setFormData(prev => ({
                ...prev,
                CustomerId: selectedOption.value,
                StateId: selectedOption.stateId || '',
                StateName: selectedOption.stateName || ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                CustomerId: '',
                StateId: '',
                StateName: ''
            }));
        }
    };

    const handleInputChange = (inputValue) => {
        return inputValue;
    };
    
    const handleChange = (event) => {
        const { name, value, type } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? !prevData[name] : value,
        }));
    };

    const [statesList, setStatesList] = useState([]);



    //Customer Fetching

    const [CustomerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState("");

    const fetchCustomers = async (inputValue = "") => {
        if (inputValue.length < 3) return [];

        try {
            const response = await fetchData("MastersAPI", `/Customer/${companyId}/${inputValue}`, token);

            if (response.responseCode === '1111') {
                return response.response_data.map(customer => ({
                    value: customer.id,
                    label: customer.name
                }));
            }
        } catch (error) {
            console.error("Error fetching customers:", error);
        }

        return [];
    };


    useEffect(() => {
        fetchCustomers();
    }, []);

    const handleCustomersChange = (selectedOption) => {
        setSelectedCustomers(selectedOption);

        // Find selected customer from full customer list (response.response_data)
        const selectedCustomerData = CustomerOptions.find(x => x.value === selectedOption.value);

        setFormData(prevData => ({
            ...prevData,
            UpdatedBy: userId,
            CompanyId: companyId,
            CustomerId: selectedOption.value,
            StateId: selectedCustomerData?.stateId || 0,
            StateName: selectedCustomerData?.stateName || ""
        }));


    };

 return (
        <div className="po-page-wrapper">
            <div className="po-page-header" style={{ background: "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)" }}>
                <div className="header-left">
                    <h1 className="page-title">Detail Purchase Order</h1>
                </div>
                <div className="header-actions">
                    <Button
                        variant="outlined"
                        component={Link}
                        to="/PurchaseOrderIndex"
                        className="btn-cancel"
                        style={{background:'#efd9e2ff'}}
                    >
                        Cancel
                    </Button>
                    {/* <Button
                        variant="contained"
                        onClick={submitData}
                        disabled={isLoading}
                        className="btn-save"
                        startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {isLoading ? "Updating..." : "Update Order"}
                    </Button>*/}
                </div> 
            </div>

            {errorMessage && (
                <div className="alert-error">
                    <svg className="alert-icon" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span>{errorMessage}</span>
                </div>
            )}

            <div className="po-content">
                <div className="form-section">
                    <div className="section-content">
                        <div className="form-row first-row">
                            <div className="form-group">
                                <label className="form-label">
                                    Customer <span className="required">*</span>
                                </label>
                                <AsyncCreatableSelect
                                    classNamePrefix="select"
                                    loadOptions={fetchOptions}
                                    onChange={handleCustomerSelect}
                                    value={selectedCustomer}
                                    onInputChange={handleInputChange}
                                    placeholder="Search or create customer..."
                                    isClearable
                                    menuPlacement="auto"
                                    menuPosition="fixed"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        menu: base => ({ ...base, zIndex: 9999 }),
                                        control: base => ({
                                            ...base,
                                            height: 38,
                                            minHeight: 38
                                        }),
                                    }}
                                />

                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    PO Number <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="PONumber"
                                    className="form-control"
                                    value={formData.PONumber}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    PO Date <span className="required">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="PODate"
                                    className="form-control"
                                    value={formData.PODate || ""}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Subcontract</label>
                                <select
                                    name="SubcontractId"
                                    className="form-control"
                                    value={selectedSubcontracters.value || formData.SubcontractId || ""}
                                    onChange={handleSubcontractersChange}
                                >
                                    <option value="">Select Subcontract</option>
                                    {SubcontractersOptions.map((item) => (
                                        <option key={item.value} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">State</label>
                                <input
                                    type="text"
                                    name="StateName"
                                    className="form-control"
                                    value={statesList.find(s => s.Id === formData.StateId)?.Name || formData.StateName || ""}
                                    readOnly
                                    placeholder="Auto-populated from Customer"
                                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                />
                            </div>

                        </div>

                        <div className="form-row" style={{ marginTop: '20px', marginBottom: '20px' }}>
                            <div className="form-group">
                                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                                    <input
                                        type="checkbox"
                                        checked={showStamping}
                                        onChange={(e) => setShowStamping(e.target.checked)}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#667eea' }}
                                    />
                                    <span>Stamping (Legal Metrology)</span>
                                </label>
                            </div>

                            <div className="form-group">
                                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                                    <input
                                        type="checkbox"
                                        checked={showAMC}
                                        onChange={(e) => setShowAMC(e.target.checked)}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#667eea' }}
                                    />
                                    <span>AMC (Annual Maintenance Contract)</span>
                                </label>
                            </div>
                        </div>

                        {(showStamping || showAMC) && (
                            <div className="date-range-container">
                                {showStamping && (
                                    <div className="date-range-group">
                                        <h3 className="range-title">Stamping Details</h3>
                                        <div className="form-row-vertical">
                                            <div className="form-group">
                                                <label className="form-label">Stamping Type</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value="Yearly"
                                                    readOnly
                                                    style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed', fontWeight: '600' }}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">From Date</label>
                                                <input
                                                    type="date"
                                                    name="StampingFromDate"
                                                    className="form-control"
                                                    value={formData.StampingFromDate || ""}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">To Date</label>
                                                <input
                                                    type="date"
                                                    name="StampingToDate"
                                                    className="form-control"
                                                    value={formData.StampingToDate || ""}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {showAMC && (
                                    <div className="date-range-group">
                                        <h3 className="range-title">AMC Details</h3>
                                        <div className="form-row-vertical">
                                            <div className="form-group">
                                                <label className="form-label">AMC Type</label>
                                                <Select
                                                    classNamePrefix="select"
                                                    options={durationTypes}
                                                    value={durationTypes.find(x => x.value === formData.AMCTypeId) || null}
                                                    onChange={(opt) =>
                                                        setFormData(prev => ({ ...prev, AMCTypeId: opt ? opt.value : '' }))
                                                    }
                                                    placeholder="Select AMC type..."
                                                    isClearable
                                                    menuPlacement="auto"
                                                    menuPosition="fixed"
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                        menu: base => ({ ...base, zIndex: 9999 }),
                                                        control: (base) => ({
                                                            ...base,
                                                            height: 38,
                                                            minHeight: 38
                                                        }),
                                                    }}
                                                />
                                                <span className="field-hint">Reminder will be sent before expiry</span>
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">From Date</label>
                                                <input
                                                    type="date"
                                                    name="AMCFromDate"
                                                    className="form-control"
                                                    value={formData.AMCFromDate || ""}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">To Date</label>
                                                <input
                                                    type="date"
                                                    name="AMCToDate"
                                                    className="form-control"
                                                    value={formData.AMCToDate || ""}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};


export default PurchaseOrderDetail;
